import { Card, Readout, cn, type StatusTone } from '@saniplus/ui';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * THE HOME SCREEN'S INSTRUMENT.
 *
 * A status swatch, the state **word**, one right-hand mono fact, the state's
 * **one number**, and a footnote. Colour, word and number change together;
 * nothing else about the panel does — which is what lets a worker answer "am I
 * on the clock?" before the phone reaches eye level.
 *
 * It is deliberately **not** the full-bleed `StateField`. `DESIGN.md`'s band
 * is for a screen that *interrupts* — a refusal, W8 — and a full-bleed alarm
 * colour that is on screen every night stops being read
 * (`W8-recovery/JUDGEMENT.md` §8, which settles the divergence between the two
 * design folders as a rule: band for refusals, in-panel readout for the
 * working home screen).
 *
 * `role="status"` on purpose: when the state changes under a worker's thumb,
 * the change is announced.
 *
 * The live region carries **no `aria-label`**, deliberately. A named live
 * region is a documented announcement hazard: VoiceOver and NVDA read the
 * region's accessible name in place of — or ahead of — the changed content,
 * so labelling this one with the state word made the word the only thing a
 * worker heard and swallowed the number that changed with it. The panel still
 * needs a name for rotor and region navigation, so the name lives in a
 * visually-hidden `<h2>` **outside** the region: announced on demand, never in
 * the way of an announcement.
 */
/** The solid field the state word sits in — fill and ink measured as a pair. */
const FIELD_TONE: Readonly<Record<StatusTone, string>> = {
  success: 'bg-status-success text-status-success-fg',
  warning: 'bg-status-warning text-status-warning-fg',
  danger: 'bg-status-danger text-status-danger-fg',
  info: 'bg-status-info text-status-info-fg',
  neutral: 'bg-status-neutral text-status-neutral-fg',
  'neutral-strong': 'bg-status-neutral-strong text-status-neutral-strong-fg',
};

/** The footnote's ink — coloured TEXT is legal in a footnote, not as a status. */
const WORD_TONE: Readonly<Record<StatusTone, string>> = {
  success: 'text-status-success-ink',
  warning: 'text-status-warning-ink',
  danger: 'text-status-danger-ink',
  info: 'text-status-info-ink',
  neutral: 'text-ink-muted',
  'neutral-strong': 'text-ink',
};

export interface InstrumentPanelProps {
  readonly tone: StatusTone;
  /** The state word, already translated: « En service », « Quart non fermé ». */
  readonly word: string;
  /** The right-hand mono fact: « depuis 21 h 54 ». */
  readonly cap?: ReactNode;
  /** The state's ONE number, already formatted. */
  readonly value?: ReactNode;
  /**
   * `duration` (66 px) for an elapsed span — the thing only this state has.
   * `clock` (40 px) for a wall time: « 22 h 00 » and « 03 h 12 » at the same
   * size are the same visual gesture at arm's length, and separating them is
   * the whole job of this screen.
   */
  readonly valueScale?: 'duration' | 'clock';
  /** The unit or qualifier beside the number: « min », « août », « DÉBUT ». */
  readonly valueUnit?: ReactNode;
  /** A screen-reader expansion — "3 heures 12 minutes", never "03 h 12". */
  readonly valueSrLabel?: string;
  /** The machine-readable instant, when the value is one. */
  readonly valueDateTime?: string;
  readonly footnote?: ReactNode;
  /** A 16 px glyph before the footnote. Decorative. */
  readonly footnoteIcon?: ReactNode;
  readonly footnoteTone?: StatusTone | 'muted';
  /**
   * The shift, folded into this panel rather than standing beside it as a
   * second card (`ShiftLine`).
   *
   * It sits **outside** `role="status"` on purpose. The live region announces
   * what CHANGED — the word and the number — and a shift that has not changed
   * would be read out again after every state transition, burying the change
   * behind four lines a worker already knows. It is still inside the panel, so
   * rotor and region navigation reach it on demand.
   */
  readonly footer?: ReactNode;
  /**
   * An 11 px uppercase label naming the footer — « PROCHAIN QUART ».
   *
   * Only for the state where the footer stops being a detail of the number
   * above it and becomes the panel's answer on its own. Every other state's
   * footer is the shift the number already refers to, and labelling that would
   * be an eyebrow over a sentence that does not need one.
   */
  readonly footerLabel?: string;
}

const FOOTNOTE_TONE: Readonly<Record<StatusTone | 'muted', string>> = {
  ...WORD_TONE,
  muted: 'text-ink-muted',
};

export function InstrumentPanel({
  tone,
  word,
  cap,
  value,
  valueScale = 'duration',
  valueUnit,
  valueSrLabel,
  valueDateTime,
  footnote,
  footnoteIcon,
  footnoteTone = 'muted',
  footer,
  footerLabel,
}: InstrumentPanelProps) {
  const { t } = useTranslation('punch');
  return (
    <Card
      padding="phone"
      /*
       * ON A SHORT VIEWPORT the panel gives back 16 px of its own padding —
       * the last measured overflow between a ~390 px-tall viewport and this
       * panel's full contents. Nothing on this screen may be dropped (the
       * history panel DESIGN.md sacrifices here no longer exists) and nothing
       * may be clipped, so the padding is what yields. It is the one value on
       * the panel that carries no information.
       *
       * `short:` (<= 700 px of viewport) rather than `sideways:` since
       * 2026-09-03: sideways is a strict subset of it, and a 360x640 phone —
       * portrait, ~500 px of usable height in a browser — has exactly the
       * landscape budget problem without being landscape.
       */
      className="short:py-2.5"
    >
      <h2 className="sr-only">{t('a11y.stateRegion')}</h2>
      <div role="status" className="min-w-0">
        {/*
          * DESIGN.md's Field-and-Word Rule: « a status is a solid field with
          * near-black text and a French word inside it. **Never a bare dot**,
          * never a coloured word alone. » This was both violations at once — a
          * 12 px swatch beside an 11 px uppercase coloured word — on the one
          * element that answers "am I on the clock?". Uppercase went with it:
          * the rule reserves caps for labels, and « ENTRÉE » loses its accent
          * at 11 px to a tired eye behind a face shield.
          *
          * It is a chip-sized field, not `packages/ui`'s full-bleed
          * `StateField`: that band is for a screen that INTERRUPTS (W8), it
          * carries its own `role="status"`, and a second live region inside
          * this one would race the first. Same tokens, same rule, one region.
          */}
        <div className="flex items-center justify-between gap-3">
          <p
            className={cn(
              'min-w-0 truncate rounded-chip px-2.5 py-1 text-subject font-semibold',
              FIELD_TONE[tone],
            )}
          >
            {word}
          </p>
          {cap === undefined ? null : (
            <Readout scale="data" tone="muted" className="shrink-0">
              {cap}
            </Readout>
          )}
        </div>

        {value === undefined ? null : (
          <div className="mt-2.5">
            <Readout
              scale={valueScale}
              {...(valueUnit === undefined ? {} : { unit: valueUnit })}
              {...(valueSrLabel === undefined ? {} : { srValue: valueSrLabel })}
              {...(valueDateTime === undefined ? {} : { dateTime: valueDateTime })}
              /*
               * DESIGN.md's landscape contract, generalised to every SHORT
               * viewport (2026-09-03): the readout drops to `readout-2`
               * (40 px). A 66 px number plus a 64 px rail, an 88 px key, the
               * tasks summary and a 68 px nav do not fit in ~500 px of
               * height, and the things that must never be pushed off are the
               * key and the tasks summary. A duration is the only scale that
               * starts at 66; `clock` is already 40 and is left alone.
               */
              className={cn(
                'text-ink',
                valueScale === 'duration' ? 'short:text-readout-2' : '',
              )}
            >
              {value}
            </Readout>
          </div>
        )}

        {footnote === undefined ? null : (
          <div className="mt-2 flex items-start gap-2 border-t border-hairline pt-2">
            {footnoteIcon === undefined ? null : (
              <span
                aria-hidden="true"
                className={cn('mt-px shrink-0', FOOTNOTE_TONE[footnoteTone])}
              >
                {footnoteIcon}
              </span>
            )}
            <p className={cn('min-w-0 text-body', FOOTNOTE_TONE[footnoteTone])}>{footnote}</p>
          </div>
        )}
      </div>

      {footer === undefined ? null : (
        <div
          className="mt-2.5 border-t border-hairline pt-2.5 short:mt-2 short:pt-2"
        >
          {footerLabel === undefined ? null : (
            <h3 className="mb-1.5 text-label uppercase text-ink-muted">{footerLabel}</h3>
          )}
          {footer}
        </div>
      )}
    </Card>
  );
}
